import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Clock, MessageSquare } from 'lucide-react';
import { companyInfo } from '../mock/data';
import { toast } from 'sonner';
import SEO from '../components/SEO';

// Web3Forms Access Key
const WEB3FORMS_ACCESS_KEY = '153572c2-262d-4749-8ba6-a2d6aaa74108';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [consultationData, setConsultationData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: ''
  });

  const [loading, setLoading] = useState(false);
  const [consultationLoading, setConsultationLoading] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: `New Contact Form: ${formData.name} - ${formData.service || 'General Inquiry'}`,
          from_name: 'Equi Pulse Website',
          ...formData
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Thank you! We have received your message and will get back to you within 24-48 hours.');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConsultationSubmit = async (e) => {
    e.preventDefault();
    setConsultationLoading(true);
    
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: `📅 New Consultation Request: ${consultationData.name} - ${consultationData.preferredDate}`,
          from_name: 'Equi Pulse Website',
          form_type: 'Consultation Booking',
          ...consultationData
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Your consultation request has been received! Our team will contact you within 24 hours to confirm the appointment.');
        setConsultationData({ name: '', email: '', phone: '', preferredDate: '', preferredTime: '' });
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Consultation form error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setConsultationLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Contact Us | Get in Touch | Equi Pulse Research"
        description="Contact Equi Pulse Research for stock market advisory services. Call +91-8982892301 or email care@equipulseresearch.com. Book a free consultation today."
        keywords="contact equi pulse, stock advisor contact, investment advisory contact, free consultation, Indore"
        url="/contact"
      />
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1423666639041-f56000c27a9a?auto=format&fit=crop&w=1920')] bg-cover bg-center opacity-10" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Contact <span className="bg-gradient-to-r from-emerald-400 to-amber-400 bg-clip-text text-transparent">Equi Pulse Research</span>
            </h1>
            <p className="text-xl text-gray-300">
              Book a free consultation with our SEBI registered research analysts today.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl border border-emerald-700/30 hover:border-amber-500/50 transition-all duration-300">
              <div className="inline-block p-3 bg-emerald-900/30 rounded-lg mb-4">
                <Mail className="w-6 h-6 text-emerald-500" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Email Us</h3>
              <a href={`mailto:${companyInfo.email}`} className="text-gray-400 hover:text-amber-400 transition-colors duration-200">
                {companyInfo.email}
              </a>
            </div>

            <div className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl border border-emerald-700/30 hover:border-amber-500/50 transition-all duration-300">
              <div className="inline-block p-3 bg-amber-900/30 rounded-lg mb-4">
                <Phone className="w-6 h-6 text-amber-500" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Call Us</h3>
              <a href={`tel:${companyInfo.phone}`} className="text-gray-400 hover:text-amber-400 transition-colors duration-200">
                {companyInfo.phone}
              </a>
            </div>

            <div className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl border border-emerald-700/30 hover:border-amber-500/50 transition-all duration-300">
              <div className="inline-block p-3 bg-emerald-900/30 rounded-lg mb-4">
                <Clock className="w-6 h-6 text-emerald-500" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Business Hours</h3>
              <p className="text-gray-400">Mon - Fri: 9:00 AM - 6:00 PM</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form and Consultation Booking */}
      <section className="py-20 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <MessageSquare className="w-8 h-8 text-emerald-500" aria-hidden="true" />
                  <h2 className="text-3xl font-bold text-white">Send Us a Message</h2>
                </div>
                <p className="text-gray-400">
                  Have questions? Fill out the form below and we&apos;ll get back to you shortly.
                </p>
              </div>

              <form onSubmit={handleContactSubmit} className="space-y-6">
                {/* Honeypot field for spam protection */}
                <input type="checkbox" name="botcheck" className="hidden" style={{ display: 'none' }} />
                
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    aria-required="true"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-emerald-700/30 rounded-lg text-white focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-200"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    aria-required="true"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-emerald-700/30 rounded-lg text-white focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-200"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    required
                    aria-required="true"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-emerald-700/30 rounded-lg text-white focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-200"
                    placeholder="+91-XXXXXXXXXX"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-300 mb-2">
                    Interested Service
                  </label>
                  <select
                    id="service"
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-emerald-700/30 rounded-lg text-white focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-200"
                  >
                    <option value="">Select a service</option>
                    <option value="Equi Rider">Equi Rider</option>
                    <option value="Equi Hunter">Equi Hunter</option>
                    <option value="Equi Master">Equi Master</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    required
                    aria-required="true"
                    rows="4"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-emerald-700/30 rounded-lg text-white focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-200 resize-none"
                    placeholder="Tell us about your investment goals..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  aria-busy={loading}
                  className="w-full px-8 py-4 bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-600 hover:to-emerald-500 disabled:from-gray-600 disabled:to-gray-600 text-white font-semibold rounded-lg shadow-lg shadow-emerald-900/50 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                  <Send className="w-5 h-5" aria-hidden="true" />
                </button>
              </form>
            </div>

            {/* Consultation Booking */}
            <div>
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-8 h-8 text-amber-500" aria-hidden="true" />
                  <h2 className="text-3xl font-bold text-white">Book a Free Consultation</h2>
                </div>
                <p className="text-gray-400">
                  Schedule a one-on-one session with our experts to discuss your investment strategy.
                </p>
              </div>

              <form onSubmit={handleConsultationSubmit} className="space-y-6">
                {/* Honeypot field for spam protection */}
                <input type="checkbox" name="botcheck" className="hidden" style={{ display: 'none' }} />
                
                <div>
                  <label htmlFor="cons-name" className="block text-sm font-medium text-gray-300 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="cons-name"
                    required
                    aria-required="true"
                    value={consultationData.name}
                    onChange={(e) => setConsultationData({ ...consultationData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-amber-700/30 rounded-lg text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all duration-200"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label htmlFor="cons-email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="cons-email"
                    required
                    aria-required="true"
                    value={consultationData.email}
                    onChange={(e) => setConsultationData({ ...consultationData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-amber-700/30 rounded-lg text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all duration-200"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="cons-phone" className="block text-sm font-medium text-gray-300 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="cons-phone"
                    required
                    aria-required="true"
                    value={consultationData.phone}
                    onChange={(e) => setConsultationData({ ...consultationData, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-amber-700/30 rounded-lg text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all duration-200"
                    placeholder="+91-XXXXXXXXXX"
                  />
                </div>

                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-gray-300 mb-2">
                    Preferred Date *
                  </label>
                  <input
                    type="date"
                    id="date"
                    required
                    aria-required="true"
                    value={consultationData.preferredDate}
                    onChange={(e) => setConsultationData({ ...consultationData, preferredDate: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-amber-700/30 rounded-lg text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all duration-200"
                  />
                </div>

                <div>
                  <label htmlFor="time" className="block text-sm font-medium text-gray-300 mb-2">
                    Preferred Time *
                  </label>
                  <select
                    id="time"
                    required
                    aria-required="true"
                    value={consultationData.preferredTime}
                    onChange={(e) => setConsultationData({ ...consultationData, preferredTime: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-amber-700/30 rounded-lg text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all duration-200"
                  >
                    <option value="">Select a time slot</option>
                    <option value="10:00 AM">10:00 AM</option>
                    <option value="11:00 AM">11:00 AM</option>
                    <option value="12:00 PM">12:00 PM</option>
                    <option value="2:00 PM">2:00 PM</option>
                    <option value="3:00 PM">3:00 PM</option>
                    <option value="4:00 PM">4:00 PM</option>
                    <option value="5:00 PM">5:00 PM</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={consultationLoading}
                  aria-busy={consultationLoading}
                  className="w-full px-8 py-4 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 disabled:from-gray-600 disabled:to-gray-600 text-white font-semibold rounded-lg shadow-lg shadow-amber-900/50 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  {consultationLoading ? 'Booking...' : 'Book Consultation'}
                  <Clock className="w-5 h-5" aria-hidden="true" />
                </button>
              </form>

              {/* WhatsApp Contact */}
              <div className="mt-8 p-6 bg-gradient-to-br from-emerald-900/20 to-slate-800 rounded-xl border border-emerald-700/30">
                <h3 className="text-lg font-semibold text-white mb-3">Prefer WhatsApp?</h3>
                <p className="text-gray-400 mb-4 text-sm">Connect with us instantly on WhatsApp for quick queries.</p>
                <a
                  href={`https://wa.me/${companyInfo.whatsapp}?text=Hello%20Equi%20Pulse!%20I'd%20like%20to%20know%20more%20about%20your%20services.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-lg transition-all duration-300"
                >
                  <MessageSquare className="w-5 h-5" aria-hidden="true" />
                  Chat on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
